import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-hope",
  title: "10 Bible verses about hope, and why none of them are optimism",
  h1: "Bible verses about hope",
  description:
    "Ten passages from the World English Bible with a note on each — because biblical hope is anchored to a person rather than to a forecast, which is why it survives evidence.",
  targets: [
    "bible verses about hope",
    "scripture about hope in hard times",
    "verses on hope and faith",
  ],
  published: "2026-09-03",
  standfirst:
    "Optimism is a prediction and two bad years disprove it. Hope in these passages is a claim about who is at the other end, which is why the writers use it from prison.",
  takeaways: [
    "Romans 5:3–5 runs suffering to perseverance to character to hope. Hope arrives last, and after the hard part rather than instead of it.",
    "Hebrews 6:19 calls it an anchor: it does not move you anywhere and it does not improve the weather. It stops you being carried off.",
    "1 Peter 1:3 grounds it in something that already happened rather than in something expected, which is what makes it unfalsifiable by a bad year.",
    "Several of these are written from prison or from exile. That is the setting the vocabulary was built for.",
  ],
  intro: [
    {
      type: "p",
      text: "Hope is eighteenth on the most-searched topic list of the largest topical Bible site, and it is one of the words that has drifted furthest in English. In ordinary use it means a wish with low confidence attached — I hope it does not rain. In these passages it is closer to certainty about something not yet visible.",
    },
    {
      type: "p",
      text: "That difference is not academic. A hope that is a forecast can be disproved by a couple of bad years, and it usually is. A hope anchored to a person survives an outcome going the wrong way, which is why the New Testament writers use the word while describing shipwrecks and imprisonments — and why {a prayer for the flat stretch} asks for that kind and not the other.",
      links: { "a prayer for the flat stretch": "/prayers/prayer-for-hope" },
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "Not only this, but we also rejoice in our sufferings, knowing that suffering produces perseverance; and perseverance, proven character; and proven character, hope; and hope doesn't disappoint us, because God's love has been poured into our hearts through the Holy Spirit who was given to us.",
        ref: "Romans 5:3–5",
      },
      note: "A chain with hope at the end rather than the beginning, which reverses how the word is normally used. Nothing here suggests the first link can be skipped. And the reason hope does not disappoint is not that circumstances resolve — it is a love already poured in, stated in the past tense, before any outcome is known.",
    },
    {
      verse: {
        text: "This hope we have as an anchor of the soul, a hope both sure and steadfast and entering into that which is within the veil,",
        ref: "Hebrews 6:19",
      },
      note: "An anchor is not an engine. It moves you nowhere and it does not calm the sea; it stops you being carried off while the weather does what it does. The image also places the anchor somewhere else entirely — inside the veil, out of sight — which is the whole difference from optimism, whose anchor is the forecast.",
    },
    {
      verse: {
        text: "Blessed be the God and Father of our Lord Jesus Christ, who according to his great mercy caused us to be born again to a living hope through the resurrection of Jesus Christ from the dead,",
        ref: "1 Peter 1:3",
      },
      note: "The hope is grounded in an event that has already happened rather than in one that is expected, which is what makes it unfalsifiable by a bad year. Living is an odd adjective for a hope and it is chosen deliberately — the letter is written to scattered people under social hostility, and a hope that is alive is one that keeps producing something.",
    },
    {
      verse: {
        text: '"Yahweh is my portion," says my soul. "Therefore I will hope in him."',
        ref: "Lamentations 3:24",
      },
      note: "Portion is an inheritance word — the share of land a family received, except that the priests were given none and told God was their portion instead. Said in a book written in the ruins of a burned city, it is a man with nothing left listing what he still has, and concluding that it is enough to hope on.",
    },
    {
      verse: {
        text: "I wait for Yahweh. My soul waits. I hope in his word.",
        ref: "Psalm 130:5",
      },
      note: "Three short clauses, from a psalm that opens out of the depths and never reports the depths receding. The hope is placed in a word rather than in a change of circumstances, and what the psalm records at the end is somebody still there in the morning — which is its idea of an answer.",
    },
    {
      verse: {
        text: "Now may the God of hope fill you with all joy and peace in believing, that you may abound in hope in the power of the Holy Spirit.",
        ref: "Romans 15:13",
      },
      note: "A blessing rather than an instruction, which matters on a day when none of this can be produced. Nobody in the sentence is being told to be hopeful; hope is described as something poured in from elsewhere. If you have none, this verse is not asking you to find some.",
    },
    {
      verse: {
        text: "Our soul has waited for Yahweh. He is our help and our shield.",
        ref: "Psalm 33:20",
      },
      note: "Plural throughout — our soul, our help, our shield — which is easy to miss and changes the verse. This is a congregation saying it together, not an individual reassuring themselves, and the waiting is a shared posture. A great deal of biblical hope is corporate in a way English devotional use tends to privatise.",
    },
    {
      verse: {
        text: 'For I know the thoughts that I think toward you," says Yahweh, "thoughts of peace, and not of evil, to give you hope and a future.',
        ref: "Jeremiah 29:11",
      },
      note: "The most-quoted hope verse in English, and the one most worth reading in place. It is in a letter to people deported to Babylon, telling them to build houses and plant gardens because the exile will last seventy years — most of its first readers died in it. The hope it offers is real and it is generational, which is a different promise from the one on the graduation card.",
    },
    {
      verse: {
        text: "looking for the blessed hope and appearing of the glory of our great God and Savior, Jesus Christ,",
        ref: "Titus 2:13",
      },
      note: "Hope here is not a feeling or an attitude but an event — the thing being looked for. That grammatical difference runs through most of the New Testament's use of the word, and it explains why it can be spoken of as sure: you can be certain about an appointment without being cheerful about the wait.",
    },
    {
      verse: {
        text: "Why are you in despair, my soul? Why are you disturbed within me? Hope in God! For I shall still praise him, the saving help of my countenance, and my God.",
        ref: "Psalm 42:11",
      },
      note: "A man talking to himself because talking to God is not currently working, and the same two questions have already been asked twice earlier in the psalm without being resolved. The repetition is the point: this is not somebody who cheered up but somebody saying the true thing again on a day he does not feel it.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "the-distinction-that-matters",
      text: "The distinction that matters",
    },
    {
      type: "p",
      text: "If your hope is a forecast, a run of bad outcomes will take it, and no amount of trying harder will put it back. If it is anchored to a person, the outcomes can go badly and the anchor holds — which is exactly the claim Hebrews 6:19 is making, and why the image is a piece of ironmongery rather than a feeling.",
    },
    {
      type: "p",
      text: "The practical measure is not whether you feel hopeful. It is whether you are still asking. That is what the psalms of lament are, structurally, and it is a lower and much more achievable bar.",
    },
    {
      type: "note",
      text: "Flatness that has lasted months, in every area at once, is more likely to be depression than a spiritual condition. It is treatable, and treating it is not a failure of hope.",
    },
  ],
  faq: [
    {
      q: "What is the difference between hope and optimism in the Bible?",
      a: "Optimism is a prediction about outcomes and can be disproved by a couple of bad years. Biblical hope is confidence about who is at the other end, which is why the New Testament writers use the word while describing prison and shipwreck.",
    },
    {
      q: "What is the best Bible verse about hope?",
      a: "Romans 15:13 is a blessing rather than an instruction, which helps on a day when you cannot produce any. Hebrews 6:19 gives the image: an anchor, which stops you being carried off rather than moving you anywhere.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so all ten are printed whole rather than referred to. Lamentations 3:24, Psalm 130:5, Psalm 33:20 and Jeremiah 29:11 render the divine name as Yahweh, where most translations have 'the LORD'.",
    },
  ],
  related: ["phone-addiction-bible-verses", "digital-sabbath"],
  relatedTopics: ["bible-verses-about-grief", "bible-verses-about-strength"],
};
